/**
 * Boards module getters
 * @packageDocumentation
 * @category Store
 */
import { GetterTree } from 'vuex';
import { BoardState as State, Board, CardsLayoutOption } from '@/types';
import { SuggestionType } from '@/types/suggestions';
import { CardHint, Card, Tag, Project, List } from '@superthread-com/api';
import { IEpic } from '@/types/epics';
import { ResourceType } from '@/types/resources';
import { DisplayedCard } from '@/types/displayedCard';
import constants from '@/utilities/constants';
import { EpicTabId } from '@/types/tabs';
import { getDefaultCardStatuses } from '@/utilities/defaultCardStatuses';
import priorityOptionsData from '@/utilities/priorityOptionsData';
import { getSprintListKey } from '@/utilities/sprintKeys';

/**
 * Get assignees for a card
 */
function getCardAssignees(card: Card, unassignedIdSuffix: string): string[] {
  return card.members && card.members.length > 0
    ? card.members.map((member) => member.user_id).filter((id): id is string => id !== undefined)
    : [unassignedIdSuffix];
}

/**
 * Ensure the assignee exists in the result structure
 */
function ensureAssigneeExists(
  result: Record<string, Record<string, { cardIds: string[] }>>,
  assigneeId: string
): void {
  if (!result[assigneeId]) {
    result[assigneeId] = {};
  }
}

/**
 * Add a card to the "no tags" category for an assignee
 */
function addCardToNoTags(
  card: Card,
  assigneeId: string,
  result: Record<string, Record<string, { cardIds: string[] }>>,
  notags: string
): void {
  if (!result[assigneeId][notags]) {
    result[assigneeId][notags] = { cardIds: [] };
  }
  if (card.id) {
    result[assigneeId][notags].cardIds.push(card.id);
  }
}

/**
 * Add a card to its tags for an assignee
 */
function addCardToTags(
  card: Card,
  assigneeId: string,
  result: Record<string, Record<string, { cardIds: string[] }>>,
  state: any
): void {
  if (!card.tags || card.tags.length === 0) return;

  card.tags.forEach((tag: Tag) => {
    if (!tag.id || !state.tags[tag.id]) return;

    if (!result[assigneeId][tag.id]) {
      result[assigneeId][tag.id] = { cardIds: [] };
    }
    if (card.id) {
      result[assigneeId][tag.id].cardIds.push(card.id);
    }
  });
}

/**
 * Process a card for its assignees, adding it to the appropriate tags
 */
function processCardForAssignees(
  card: Card,
  cardAssignees: string[],
  result: Record<string, Record<string, { cardIds: string[] }>>,
  notags: string,
  state: any
): void {
  if (!card.id) return;

  cardAssignees.forEach((assigneeId) => {
    ensureAssigneeExists(result, assigneeId);

    if (!card.tags || card.tags.length === 0) {
      addCardToNoTags(card, assigneeId, result, notags);
      return;
    }

    addCardToTags(card, assigneeId, result, state);
  });
}

/**
 * Initialize tag-assignee structure for storing card IDs
 */
function initializeTagAssigneeStructure(
  tags: { order?: string[] },
  assignees: string[],
  result: Record<string, Record<string, { cardIds: string[] }>>
): void {
  tags?.order?.forEach((tagId: string) => {
    result[tagId] = {};
    assignees.forEach((assigneeId: string) => {
      result[tagId][assigneeId] = { cardIds: [] };
    });
  });
}

/**
 * Process a card and add it to the appropriate tag-assignee buckets
 */
function addCardToTagAssigneeBuckets(
  card: Card | undefined,
  notagsId: string,
  noassigneesId: string,
  result: Record<string, Record<string, { cardIds: string[] }>>
): void {
  if (!card?.id) return;

  // Get tags for this card
  const cardTags = getCardTagIds(card, notagsId);

  // Get assignees for this card
  const cardAssignees = getCardAssignees(card, noassigneesId);

  // Add card to appropriate buckets
  addCardToResults(card.id, cardTags, cardAssignees, result);
}

/**
 * Get the tag IDs for a card
 */
function getCardTagIds(card: Card, notagsId: string): string[] {
  return card.tags && card.tags.length > 0
    ? card.tags.map((tag) => tag.id).filter((id): id is string => id !== undefined)
    : [notagsId];
}

/**
 * Add a card to the results structure for each tag-assignee combination
 */
function addCardToResults(
  cardId: string,
  tagIds: string[],
  assigneeIds: string[],
  result: Record<string, Record<string, { cardIds: string[] }>>
): void {
  tagIds.forEach((tagId) => {
    ensureTagExists(result, tagId);

    assigneeIds.forEach((assigneeId) => {
      ensureAssigneeExistsForTag(result, tagId, assigneeId);
      result[tagId][assigneeId].cardIds.push(cardId);
    });
  });
}

/**
 * Ensure a tag exists in the results structure
 */
function ensureTagExists(
  result: Record<string, Record<string, { cardIds: string[] }>>,
  tagId: string
): void {
  if (!result[tagId]) {
    result[tagId] = {};
  }
}

/**
 * Ensure an assignee exists for a tag in the results structure
 */
function ensureAssigneeExistsForTag(
  result: Record<string, Record<string, { cardIds: string[] }>>,
  tagId: string,
  assigneeId: string
): void {
  if (!result[tagId][assigneeId]) {
    result[tagId][assigneeId] = { cardIds: [] };
  }
}

export default {
  /** Returns currently opened board. */
  currentBoard: (state, getters): Board | {} => state.flatBoards[getters.currentBoardId] || {},
  /** Returns currently opened board id */
  currentBoardId: (state): string => state.currentBoardId,
  /** Returns the list of added boards. */
  lists: (state) => state.lists,
  /** Returns the list of board ids for specific projectId. */
  getProjectBoards: (state) => (projectId: string | number) => state.projectBoards[projectId] || [],

  getFlatBoards: (state) => state.flatBoards,

  boardsListsIds: (state) => state.boardsListsIds,

  /** Returns  the id of project in which board is being added. */
  getNewBoardProjectId: (state): string | undefined => state.newBoardProjectId,
  getCardById:
    (state): any =>
    (cardId: string) =>
      state.cards[cardId] ?? {},
  getBoardById:
    (state): any =>
    (boardId: string = state.currentBoardId) =>
      state.flatBoards[boardId] ?? {},
  getViewById:
    (state): any =>
    (viewId: string = state.selectedView) =>
      [...(state.privateViews || []), ...(state.publicviews || [])].find((v) => v.id == viewId) ??
      {},

  /** Returns id of the list in which card is creating. */
  getListDraftCardCreateView: (state): string | null | undefined => state.listDraftCardCreateView,
  /** Returns a data of dragged entity. */
  draggedEntity: (state): any => state.draggedEntity,
  /** Returns id of element in in which dragging was finished. */
  dropTargetId: (state): string | undefined => state.dropTargetId,
  /** Returns the board that is expanded in the list of boards. */
  boardExpanded:
    (state): any =>
    (boardId: string) =>
      state.expandedBoardIds[boardId],
  /** Returns the flag that indicates whether boards are loading. */
  loadingBoards: (state): boolean => state.loadingBoards,
  /** Returns the flag that indicates when the board's data is needed to be removed. */
  boardViewNoDestroy: (state): boolean => state.boardViewNoDestroy,
  /** Returns the flag for opening list's input when there aren't any list on the board. */
  draftListInputVisibleOnOpen: (state): boolean => state.draftListInputVisibleOnOpen,
  /** Returns current board list view type (list or grid) */
  getBoardListView: (state): string | undefined => state.boardListView,
  /** Returns object of the tag that needs to be edited */
  getCurrentTagForEdit: (state): any => state.currentTagForEdit,
  /** Returns the flag that indicates is popup for uplading image opened */
  getShowImageUploadPopup: (state): boolean => state.showImageUploadPopup,
  /** Returns id of card which have opened dropdown for edit card. */
  getCurrentCardIdActionEdit: (state): string => state.currentCardIdActionEdit,
  /** Returns id of list which have opened dropdown for edit list. */
  getCurrentListIdActionEdit: (state): string | null => state.currentListIdActionEdit,
  /** Returns current board view type (list or grid) */
  cardsLayout:
    (state, getters) =>
    (boardId: string = getters.currentBoardId) => {
      let layout = state.cardsLayout[boardId] || state.flatBoards[boardId]?.layout || '';
      if (!layout) {
        const { value } = getters.cardsLayoutOptions.find(
          (option: CardsLayoutOption) => option.default
        );
        layout = value;
      }
      return layout;
    },
  getCardsLayoutForBoard:
    (state): any =>
    (boardId: string) =>
      state.cardsLayout[boardId] || state.flatBoards[boardId]?.layout || 'board',
  getLocalBoardLayoutForBoard: (state) => (boardId: string) => state.cardsLayout[boardId],
  cardsLayoutOptions: (state): CardsLayoutOption[] => state.cardsLayoutOptions,
  getCurrentCardActionEdit: (state): any => state.cards[state.currentCardIdActionEdit] || {},
  currentCardForPopup: (state): any => state.cards[state.currentCardIdForPopup] || {},
  /** Returns object conaining the meta of removed board list  */
  removedListMeta: (state) => state.removedListMeta,
  /** Returns object conaining the meta of removed card  */
  removedCardMeta: (state) => state.removedCardMeta,
  getAttachments: (state) => (id: number, prefix: string) =>
    state.attachments[`${prefix}-${id}`] || [],
  /** Returns whether any of attachments is still uploading */
  getAreAttachmentsUploading: (state) => {
    const { attachments } = state;
    return Object.keys(attachments).some((key) =>
      attachments[key].some((a: any) => a.uploadProgress)
    );
  },
  getCardAutoFocus: (state): boolean => state.cardAutoFocus,
  getPreviewPopoverKey: (state): boolean => state.previewPopoverKey,
  getAttachmentTargetCardId: (state) => state.attachmentTargetCardId,
  getCardAttachmentsLoaded: (state): boolean => state.cardAttachmentsLoaded,
  archivedBoards: (state) => state.archivedBoards,
  archivedLists: (state) => state.archivedLists,
  archivedCards: (state) => state.archivedCards,
  getNewBoardTitle: (state) => state.newBoardTitle,
  getCurrentCardIdMemberDropdown: (state): string => state.currentCardIdMemberDropdown,
  currentCardIdForPopup: (state): string => state.currentCardIdForPopup,
  getCurrentCardMembersDropdown: (state): any => state.currentCardMembersDropdown,
  previewProject: (state) => state.previewProject,
  getLinkedCardType: (state): string => state.linkedCardType,
  getReportInfoData: (state) => state.reportInfoData,
  projectPreviewBoards: (state) => state.projectPreviewBoards,
  displayProjectPreviewBoards: (state) => state.displayProjectPreviewBoards,
  getCardAttachmentUploadingId: (state) => state.cardAttachmentUploadingId,
  getTimeperoidOptions: (state) => state.timeperiodOptions,
  getShowDuplicateCardPopup: (state): string => state.showDuplicateCardPopup,
  getShowSetParentCardPopup: (state): string => state.showSetParentCardPopup,
  /** Returns the flag that indicates is card dragging just finished */
  getDraggingEnd: (state): boolean => state.draggingEnd,
  getListCards: (state) => state.listCards,
  getCards: (state) => state.cards,
  cardsFuse: (state) => state.cardsFuse,
  /** Filters epics out of cards on state. */
  getEpics: (state) => {
    const epics: { [key: string]: IEpic } = {};
    for (const c of Object.values(state.cards as Card[] | IEpic[])) {
      if (c.type === ResourceType.Epic && !c?.archived?.user_id) {
        epics[c.id] = c;
      }
    }
    return epics;
  },
  getDisplayedCards: (state) => state.displayedCards,
  getIsCardDisplayed: (state) => (cardId: string) =>
    state.displayedCards.some((c) => c.id === cardId),
  getIsAnyCardDisplayed: (state) => state.displayedCards.length > 0,
  getLayoutOfDisplayedCard: (state) => (cardId: string) =>
    state.displayedCards.find((card: DisplayedCard) => card.id === cardId)?.layout ||
    state.currentCardLayout,
  getIsAnyCardDisplayedInLayout: (state) => (layout: string) =>
    state.displayedCards.some((card: DisplayedCard) => card.layout === layout),
  getCommentActivityFilterOfDisplayedCard: (state, globalGetters) => (cardId: string) =>
    state.displayedCards.find((card: DisplayedCard) => card.id === cardId)
      ?.comment_activity_filter || globalGetters.getCardCommentActivityFilter,
  getCommentActivityOrderOfDisplayedCard: (state, globalGetters) => (cardId: string) =>
    state.displayedCards.find((card: DisplayedCard) => card.id === cardId)
      ?.comment_activity_order || globalGetters.getCommentsOrder,
  getDisplayedCardListCards: (state) => (cardId: string) =>
    state.displayedCards.find((card: DisplayedCard) => card.id === cardId)?.list_cards || [],
  /** Returns the list of checklist ids that have collapsed state active */
  getCollapsedChecklistIds: (state) => state.collapsedChecklistIds,
  /** Returns the list of checklist ids that have checklist items hidden */
  getHiddenItemsChecklistIds: (state) => state.hiddenItemsChecklistIds,
  boardsFilters: (state) => state.boardsFilters,
  boardFilters: (state) => (boardId: number) => state.boardsFilters[boardId],
  boardsPills: (state) => (boardId: number) => state.boardsPills[boardId] || [],
  getTags: (state) => state.tags,
  /** Returns the list of tag ids for specific projectId. */
  getProjectTags: (state) => (projectId: string | number) => state.projectTags[projectId] || [],
  /** Returns the list of tag ids for specific teamId. */
  getWorkspaceTags: (state) => (teamId: string | number) => state.workspaceTags[teamId] || [],
  /** Returns the object of project tags. */
  projectTags: (state) => state.projectTags || [],
  getCardCommentAttachmentUploadPopup: (state): object => state.cardCommentAttachmentUploadPopup,
  getCardDescriptionAttachmentUploadPopup: (state): object =>
    state.cardDescriptionAttachmentUploadPopup,
  lastHoveredListId: (state) => state.lastHoveredListId,
  lastHoveredCardId: (state) => state.lastHoveredCardId,
  lastHoveredDisplayedCardId: (state) => state.lastHoveredDisplayedCardId,
  boardViewMouseSuspended: (state) => state.boardViewMouseSuspended,
  selectedCardId: (state) => state.selectedCardId,
  lastOpenedBoardId: (state): string => state.lastOpenedBoardId,
  /** Returns the flag for checking if checklist or checklist item input is active. */
  getChecklistInputActive: (state) => (checklistId: string) =>
    state.checklistInputActive[checklistId] || false,
  getChecklistsInputActive: (state) => state.checklistInputActive,
  getChecklistItemInputActive: (state) => (checklistItemId: string) =>
    state.checklistItemInputActive[checklistItemId] || false,
  getChecklistItemsInputActive: (state) => state.checklistItemInputActive,
  getTimelineZoom: (state) => state.timelineZoom,
  getTimelineSidebarWidth: (state) => state.timelineSidebarWidth,
  getCardWidth: (state) => state.cardWidth,
  cardsSchema: (state) => state.cardsSchema,
  getCardIdForMove: (state) => state.cardIdForMove,
  // Quick card
  getQuickCardFilesToUpdate: (state) => state.quickCardFilesToUpdate,
  getMembersToAddQuickCard: (state) => state.quickCardObject.members,
  getQuickCardProjectId: (state) => state.quickCardObject.projectId,
  getQuickCard: (state) => state.quickCardObject,
  // Quick child card embed
  getQuickChildCardFilesToUpdate: (state) => state.quickChildCardFilesToUpdate,
  getMembersToAddQuickChildCard: (state) => state.quickChildCardObject.members,
  getQuickChildCardProjectId: (state) => state.quickChildCardObject.projectId,
  getQuickChildCard: (state) => state.quickChildCardObject,
  getChildCardCreation: (state) => state.childCardCreation,
  getCardInEpicCreation: (state) => state.cardInEpicCreation,
  getCardInNoteCreation: (state) => state.cardInNoteCreation,
  getCardSliderActive: (state) => state.cardSliderActive,
  getBoardSortBy:
    (state, getters) =>
    (boardId = getters.currentBoardId): string => {
      let sortBy = `${state.boardSortBy[boardId] || ''}`;
      const isInEpicBoard = boardId.startsWith(constants.inEpicBoardIdPrefix);

      if (!sortBy) {
        const { value } = getters.boardSortByOptions.find((option: CardsLayoutOption) =>
          isInEpicBoard ? option.inEpicBoardDefault : option.default
        );
        sortBy = value;
      }

      return sortBy;
    },
  boardSortByOptions: (state): CardsLayoutOption[] => state.boardSortByOptions,
  getShowPriorityDropdown: (state): boolean => state.priorityDropdown,
  getShowCardPriorityPopup: (state): boolean => state.showCardPriorityPopup,
  getBoardGroupBy:
    (state, getters) =>
    (boardId = getters.currentBoardId): string => {
      let groupBy = `${state.boardGroupBy[boardId] || ''}`;
      const isInEpicBoard = boardId.startsWith(constants.inEpicBoardIdPrefix);

      if (!groupBy) {
        const { value } = getters.boardGroupByOptions.find((option: CardsLayoutOption) =>
          isInEpicBoard ? option.inEpicBoardDefault : option.default
        );
        groupBy = value;
      }

      return groupBy;
    },
  boardGroupByOptions: (state): CardsLayoutOption[] => state.boardGroupByOptions,
  getCardsByCurrentBoardId:
    (state, getters) =>
    (boardId = getters.currentBoardId) =>
      Object.values(state.cards).filter((c: any) => c.board_id === boardId) || [],
  /** Gets all the cards that are added to epic. */
  getCardsByEpicId: (state) => (epicId: string) => {
    const cards: Record<string, Card | IEpic> = {};
    for (const key in state.cards) {
      if (state.cards[key].epic?.id === epicId) {
        cards[key] = state.cards[key];
      }
    }
    return cards;
  },
  getCardsByCurrentSprintId:
    (state, getters) =>
    (boardId = getters.currentBoardId) =>
      Object.values(state.cards).filter((c: any) => c.sprint_id === boardId) || [],
  boardsCardsIds: (state) => {
    const boardsCardsIds: Record<string, string[]> = {};
    for (const [boardId, listIds] of Object.entries(state.boardsListsIds)) {
      const cards: string[] = [];
      for (const listId of listIds) {
        const listCards = state.listCards[listId];
        if (listCards) {
          cards.push(...listCards);
        }
      }
      boardsCardsIds[boardId] = cards;
    }
    return boardsCardsIds;
  },
  getBoardsCardsIdsByBoardId:
    (state, getters) =>
    (boardId: string = getters.currentBoardId): string[] => {
      return getters.boardsCardsIds[boardId] || [];
    },

  getBoardGroupByObject: (state, getters) => (boardId: string) => {
    return {
      assignee: getters.groupByAssignees(boardId),
      tag: getters.groupByTags(boardId),
      statusType: getters.groupByStatusType(boardId),
      priority: getters.groupByPriority(boardId),
      epic: getters.groupByEpics(boardId),
      none: getters.groupByNone(boardId),
      space: getters.groupBySpace(boardId),
    };
  },

  getBoardSubGroupByObject: (state, getters) => (boardId: string) => {
    return {
      assignee: {
        tag: getters.groupByAssigneesTags(boardId),
        status: getters.groupByAssigneesStatus(boardId),
      },
      tag: {
        assignee: getters.groupByTagsAssignees(boardId),
        status: getters.groupByTagsStatus(boardId),
      },
      status: {
        assignee: getters.groupByStatusAssignees(boardId),
        tag: getters.groupByStatusTags(boardId),
      },
      priority: {
        assignee: getters.groupByPriorityAssignees(boardId),
        tag: getters.groupByPriorityTags(boardId),
        status: getters.groupByPriorityStatus(boardId),
      },
      epic: {
        assignee: getters.groupByEpicsAssignees(boardId),
        tag: getters.groupByEpicsTags(boardId),
        status: getters.groupByEpicsStatus(boardId),
      },
    };
  },
  getCurrentGroupByListIdCardActionEdit: (state): string =>
    state.currentGroupByListIdCardActionEdit,
  getCurrentCardUniqueRowId: (state): string => state.currentCardUniqueRowId,
  getCardTemplates: (state) => state.cardTemplates,
  getCardTemplateDraft: (state) => state.cardTemplateDraft,
  getCardTemplateChildCards: (state) => state.cardTemplateDraft.card_child_cards,
  getQCChildCards: (state) => state.quickCardObject.childCards,
  getSuggestions: (state) => (cardId: string) => state.cards[cardId]?.hints || [],
  getQCSuggestions: (state) => state.quickCardObject?.hints || [],
  getTagSuggestions: (state) => (cardId: string) =>
    state.cards[cardId]?.hints?.filter(({ type }: CardHint) => type === SuggestionType.tag) || [],
  getQCTagSuggestions: (state) =>
    state.quickCardObject?.hints?.filter(({ type }: CardHint) => type === SuggestionType.tag) || [],
  getDuplicateSuggestions: (state) => (cardId: string) =>
    state.cards[cardId]?.hints?.filter(({ type }: CardHint) => type === SuggestionType.relation) ||
    [],
  getQCDuplicateSuggestions: (state) =>
    state.quickCardObject?.hints?.filter(
      ({ type }: CardHint) => type === SuggestionType.relation
    ) || [],
  getShowSuggestionsPlaceholder: (state) => state.showSuggestionsPlaceholder,
  getQCShowSuggestionsPlaceholder: (state) => state.qcShowSuggestionsPlaceholder,
  getNewBoardObject: (state) => state.newBoardObject,
  getSprintSettings: (state) => (projectId: string) => state.sprintSettings[projectId] || {},
  getSpaceSprints: (state) => (projectId: string | number) => state.spaceSprints[projectId] || {},
  getSprints: (state) => state.spaceSprints,
  getSprintLists: (state) => (listId: string | number) => state.sprintLists[listId] || [],
  getSprintForEditId: (state) => state.sprintForEditId,
  getEpicSelectedTabId: (state) => (cardId: string) =>
    state.epicSelectedTabId[cardId] || EpicTabId.Details,

  getBoardSubGroupBy:
    (state, getters) =>
    (boardId = getters.currentBoardId): string => {
      let subGroupBy = `${state.boardSubGroupBy[boardId] || ''}`;
      const isInEpicBoard = boardId.startsWith(constants.inEpicBoardIdPrefix);

      if (!subGroupBy) {
        const option = getters.boardGroupByOptions.find((option: CardsLayoutOption) =>
          isInEpicBoard ? option.inEpicBoardDefault : option.default
        );
        const value = option ? option.value : 'status';
        subGroupBy = value;
      }

      return subGroupBy;
    },

  getSubGroupByListOrder: (state) => (key: string, subGroup: string) => {
    return Object.keys(state.cardsSubGroupByOrder[key]?.[subGroup] || {});
  },

  getSubGroupByBoardId: (state) => (boardId: string, groupBy: string, subGroupBy: string) => {
    return state.cardsSubGroupByOrder[boardId]?.[groupBy]?.[subGroupBy] || {};
  },

  getListExpanded: (state) => (boardId: string, listId: string) => {
    return state.listsExpanded[boardId]?.[listId] ?? true;
  },

  groupByTags: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const projectTags = getters.getProjectTags(getters.getCurrentProjectId) ?? [];
    const workspaceTags = getters.getWorkspaceTags(getters.getTeamID);
    const projectWorkspaceTags = [...projectTags, ...workspaceTags];
    const notags = `${boardId}-notags`;

    // Ensure correct type
    const groupedCards: Record<string, { cardIds: string[] }> = {};

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      if (!card.tags || card.tags.length === 0) {
        if (!groupedCards[notags]) {
          groupedCards[notags] = { cardIds: [] };
        }
        if (card.id) {
          groupedCards[notags].cardIds.push(card.id);
        }
      } else {
        card.tags.forEach((tag: Tag) => {
          if (!tag.id || !state.tags[tag.id]) return;

          if (!groupedCards[tag.id]) {
            groupedCards[tag.id] = { cardIds: [] };
          }
          if (card.id) {
            groupedCards[tag.id].cardIds.push(card.id);
          }
        });
      }
    });

    const order = Array.from(new Set([...projectWorkspaceTags, notags]));

    return { ...groupedCards, order };
  },

  groupByAssignees: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const noassignees = `${boardId}-unassigned`;

    const groupedCards: Record<string, { cardIds: string[] }> = {};

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      if (!card.members || card.members.length === 0) {
        if (!groupedCards[noassignees]) {
          groupedCards[noassignees] = { cardIds: [] };
        }
        if (card.id) {
          groupedCards[noassignees].cardIds.push(card.id);
        }
      } else {
        card.members.forEach((member) => {
          if (!member.user_id) return;

          if (!groupedCards[member.user_id]) {
            groupedCards[member.user_id] = { cardIds: [] };
          }
          if (card.id) {
            groupedCards[member.user_id].cardIds.push(card.id);
          }
        });
      }
    });

    const order = Array.from(new Set([...getters.getAssigneeLists(boardId), noassignees]));

    return { ...groupedCards, order };
  },

  groupByStatusType: (state, getters) => (boardId: string) => {
    const groupedCards: Record<string, { cardIds: string[] }> = {};
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const statusTypeListIds = Object.keys(getDefaultCardStatuses()).filter(
      (status) => status !== 'rolledover'
    );

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      statusTypeListIds.forEach((listId) => {
        if (listId === card.status || (listId === 'backlog' && !card.status)) {
          if (!groupedCards[listId]) {
            groupedCards[listId] = { cardIds: [] };
          }
          if (card.id) {
            groupedCards[listId].cardIds.push(card.id);
          }
        }
      });
    });

    const order = [...statusTypeListIds];

    return { ...groupedCards, order };
  },

  groupByPriority: (state, getters) => (boardId: string) => {
    const groupedCards: Record<string, { cardIds: string[] }> = {};
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const priorityListIds = priorityOptionsData.map((priority) => priority.id);

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      const cardPriority = card.priority ?? 0;

      priorityListIds.forEach((listId) => {
        if (listId === cardPriority) {
          if (!groupedCards[listId]) {
            groupedCards[listId] = { cardIds: [] };
          }
          if (card.id) {
            groupedCards[listId].cardIds.push(card.id);
          }
        }
      });
    });

    return { ...groupedCards, order: priorityListIds };
  },

  groupByNone: (state, getters) => (boardId: string) => {
    const groupedCards: Record<string, { cardIds: string[] }> = {};
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    groupedCards['none'] = { cardIds: cards };
    return { ...groupedCards, order: ['none'] };
  },

  groupByEpics: (state, getters) => (boardId: string) => {
    const groupedCards: Record<string, { cardIds: string[] }> = {};
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const epics = [...Object.keys(getters.getEpics), `${boardId}-noproject`];

    // Initialize all epics with empty card arrays
    epics.forEach((epicId) => {
      groupedCards[epicId] = { cardIds: [] };
    });

    const addCardToGroup = (epicId: string, cardId: string) => {
      if (cardId) {
        groupedCards[epicId].cardIds.push(cardId);
      }
    };

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      epics.forEach((epicId) => {
        const isNoProjectGroup = epicId.includes('noproject') && !card.epic?.id;
        const isMatchingEpic = epicId === card.epic?.id;

        if (card.id && (isNoProjectGroup || isMatchingEpic)) {
          addCardToGroup(epicId, card.id);
        }
      });
    });

    return { ...groupedCards, order: epics };
  },

  groupBySpace: (state, getters) => (boardId: string) => {
    const groupedCards: Record<string, { cardIds: string[] }> = {};
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const spaceIds = getters.getProjectsForEpic(boardId);

    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      spaceIds.forEach((spaceId: string) => {
        if (spaceId === card.project_id) {
          if (!groupedCards[spaceId]) {
            groupedCards[spaceId] = { cardIds: [] };
          }
          if (card.id) {
            groupedCards[spaceId].cardIds.push(card.id);
          }
        }
      });
    });

    return { ...groupedCards, order: spaceIds };
  },

  getAssigneeLists: (state, getters) => (boardId: string) => {
    const board = getters.getBoardById(boardId);
    const isEpicBoard = board.id === constants.epicsBoardId;

    const inactiveMembers = new Set(
      getters.getInactiveTeamMembers ? Object.keys(getters.getInactiveTeamMembers) : []
    );
    const projectId = board.project_id ?? getters.getCurrentProjectId;
    const projectMembers = getters.getProject(projectId)?.members ?? [];
    const teamMembers = getters.getTeamMembers ?? {};

    const activeMembers = isEpicBoard ? Object.values(teamMembers) : projectMembers;

    const activeMemberIds = activeMembers.reduce((acc: string[], assignee: any) => {
      // Handle both User and Member types safely
      let memberId;
      if ('user_id' in assignee) {
        memberId = assignee.user_id;
      } else if ('id' in assignee) {
        memberId = assignee.id;
      }

      if (memberId && !inactiveMembers.has(memberId)) {
        acc.push(memberId);
      }
      return acc;
    }, []);

    return [...Object.keys(teamMembers), ...activeMemberIds];
  },

  getProjectsForEpic: (state, getters) => (boardId: string) => {
    const projectsArray: Project[] = [];

    // Add null check for boardId
    if (!boardId) return projectsArray;

    const epicId = boardId.replace(constants.inEpicBoardIdPrefix, '')?.split('-')[1] || '';

    if (!epicId) return projectsArray;

    const epicCards = Object.values(getters.getCardsByEpicId(epicId)) || [];

    const addedProjectIds = new Set();
    (epicCards as Card[]).forEach((card: Card) => {
      const project = getters.getTeamProjects.find((proj: Project) => proj.id === card.project_id);

      if (project && !addedProjectIds.has(project.id)) {
        projectsArray.push(project);
        addedProjectIds.add(project.id);
      }
    });

    return projectsArray.map((project) => project.id);
  },

  getGroupByListOrder: (state) => (boardId: string) => {
    return state.groupByListOrder[boardId] ?? {};
  },

  getGroupByCardOrder: (state) => (boardId: string, groupBy: string, listId: string) => {
    return state.groupByCardOrder[boardId]?.[groupBy]?.[listId] ?? [];
  },

  // Group by Tags -> Assignees
  groupByTagsAssignees: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const tags = getters.groupByTags(boardId);
    const noassignees = `${boardId}-unassigned`;

    // Result will be { tagId: { assigneeId: { cardIds: [] } }, order: string[] }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...getters.getAssigneeLists(boardId), noassignees]));

    // Initialize structure for all tags and assignees
    const assigneesList = [...getters.getAssigneeLists(boardId), noassignees];
    initializeTagAssigneeStructure(tags, assigneesList, result);

    // Process all cards
    const notags = `${boardId}-notags`;
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      addCardToTagAssigneeBuckets(card, notags, noassignees, result);
    });

    return result;
  },

  // Group by Assignees -> Tags
  groupByAssigneesTags: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const assignees = getters.groupByAssignees(boardId);
    const projectTags = getters.getProjectTags(getters.getCurrentProjectId) ?? [];
    const workspaceTags = getters.getWorkspaceTags(getters.getTeamID);
    const projectWorkspaceTags = [...projectTags, ...workspaceTags];
    const notags = `${boardId}-notags`;
    const unassignedId = `${boardId}-unassigned`;

    // Result will be { assigneeId: { tagId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...projectWorkspaceTags, notags]));

    // Initialize structure for all assignees
    assignees?.order?.forEach((assigneeId: string) => {
      result[assigneeId] = {};

      // Initialize all tags for this assignee
      [...projectWorkspaceTags, notags].forEach((tagId: string) => {
        result[assigneeId][tagId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get assignees for this card
      const cardAssignees = getCardAssignees(card, unassignedId);

      // Add card to appropriate tag for each assignee
      processCardForAssignees(card, cardAssignees, result, notags, state);
    });

    return result;
  },

  // Group by Assignees -> Status
  groupByAssigneesStatus: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const assignees = getters.groupByAssignees(boardId);
    const board = getters.getBoardById(boardId);
    const unassignedId = `${boardId}-unassigned`;

    // Result will be { assigneeId: { statusId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    if (board?.sprint_id) {
      result.order = board.lists.map((list: List) => list.id);
    } else {
      result.order = board?.list_order ?? [];
    }

    // Initialize structure for all assignees
    assignees?.order?.forEach((assigneeId: string) => {
      result[assigneeId] = {};

      // Initialize all statuses for this assignee
      board?.list_order?.forEach((statusId: string) => {
        result[assigneeId][statusId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get assignees for this card
      const cardAssignees = getCardAssignees(card, unassignedId);

      // Get status for this card
      const cardStatus = card.list_id ?? board?.list_order?.[0];
      if (!cardStatus) return;

      // Add card to appropriate status for each assignee
      cardAssignees.forEach((assigneeId) => {
        if (!result[assigneeId]) {
          result[assigneeId] = {};
        }

        if (!result[assigneeId][cardStatus]) {
          result[assigneeId][cardStatus] = { cardIds: [] };
        }
        if (card.id) {
          result[assigneeId][cardStatus].cardIds.push(card.id);
        }
      });
    });

    return result;
  },

  // Group by Tags -> Status
  groupByTagsStatus: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const tags = getters.groupByTags(boardId);
    const board = getters.getBoardById(boardId);

    // Result will be { tagId: { statusId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    if (board?.sprint_id) {
      result.order = board.lists.map((list: List) => list.id);
    } else {
      result.order = board?.list_order ?? [];
    }

    // Initialize structure for all tags
    tags?.order?.forEach((tagId: string) => {
      result[tagId] = {};

      // Initialize all statuses for this tag
      board?.list_order?.forEach((statusId: string) => {
        result[tagId][statusId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get tags for this card
      const cardTags =
        card.tags && card.tags.length > 0
          ? card.tags.map((tag) => tag.id).filter((id): id is string => id !== undefined)
          : [`${boardId}-notags`];

      // Get status for this card
      const cardStatus = card.list_id ?? board?.list_order?.[0];
      if (!cardStatus) return;

      // Add card to appropriate status for each tag
      cardTags.forEach((tagId) => {
        if (!result[tagId]) {
          result[tagId] = {};
        }

        if (!result[tagId][cardStatus]) {
          result[tagId][cardStatus] = { cardIds: [] };
        }
        if (card.id) {
          result[tagId][cardStatus].cardIds.push(card.id);
        }
      });
    });

    return result;
  },

  // Group by Status -> Assignees
  groupByStatusAssignees: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const board = getters.getBoardById(boardId);
    const noassignees = `${boardId}-unassigned`;
    const listIds = board?.list_order ?? (board?.lists ?? []).map((list: List) => list.id);

    // Result will be { statusId: { assigneeId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...getters.getAssigneeLists(boardId), noassignees]));

    // Initialize structure for all statuses
    listIds?.forEach((statusId: string) => {
      result[statusId] = {};

      // Initialize all assignees for this status
      [...getters.getAssigneeLists(boardId), noassignees].forEach((assigneeId: string) => {
        result[statusId][assigneeId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get status for this card
      const cardStatus = card.sprint_id
        ? getSprintListKey(card.project_id, card.sprint_id, card.list_id)
        : (card.list_id ?? board?.list_order?.[0]);
      if (!cardStatus) return;

      // Get assignees for this card
      const cardAssignees = getCardAssignees(card, noassignees);

      // Add card to appropriate assignee for this status
      if (!result[cardStatus]) {
        result[cardStatus] = {};
      }

      cardAssignees.forEach((assigneeId) => {
        if (!result[cardStatus][assigneeId]) {
          result[cardStatus][assigneeId] = { cardIds: [] };
        }
        if (card.id) {
          result[cardStatus][assigneeId].cardIds.push(card.id);
        }
      });
    });

    return result;
  },

  // Group by Status -> Tags
  groupByStatusTags: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const board = getters.getBoardById(boardId);
    const projectTags = getters.getProjectTags(getters.getCurrentProjectId) ?? [];
    const workspaceTags = getters.getWorkspaceTags(getters.getTeamID);
    const projectWorkspaceTags = [...projectTags, ...workspaceTags];
    const notags = `${boardId}-notags`;
    const listIds = board?.list_order ?? (board?.lists ?? []).map((list: List) => list.id);

    // Result will be { statusId: { tagId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...projectWorkspaceTags, notags]));

    // Initialize structure for all statuses
    listIds?.forEach((statusId: string) => {
      result[statusId] = {};

      // Initialize all tags for this status
      [...projectWorkspaceTags, notags].forEach((tagId: string) => {
        result[statusId][tagId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get status for this card
      const cardStatus = card.sprint_id
        ? getSprintListKey(card.project_id, card.sprint_id, card.list_id)
        : (card.list_id ?? board?.list_order?.[0]);
      if (!cardStatus) return;

      // Add card to appropriate tag for this status
      if (!result[cardStatus]) {
        result[cardStatus] = {};
      }

      if (!card.tags || card.tags.length === 0) {
        if (!result[cardStatus][notags]) {
          result[cardStatus][notags] = { cardIds: [] };
        }
        if (card.id) {
          result[cardStatus][notags].cardIds.push(card.id);
        }
      } else {
        card.tags.forEach((tag: Tag) => {
          if (!tag.id || !state.tags[tag.id]) return;

          if (!result[cardStatus][tag.id]) {
            result[cardStatus][tag.id] = { cardIds: [] };
          }
          if (card.id) {
            result[cardStatus][tag.id].cardIds.push(card.id);
          }
        });
      }
    });

    return result;
  },

  // Group by Priority -> Assignees
  groupByPriorityAssignees: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const priorities = getters.groupByPriority(boardId);
    const noassignees = `${boardId}-unassigned`;

    // Result will be { priorityId: { assigneeId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...getters.getAssigneeLists(boardId), noassignees]));

    // Initialize structure for all priorities
    priorities?.order?.forEach((priorityId: string) => {
      result[priorityId] = {};

      // Initialize all assignees for this priority
      [...getters.getAssigneeLists(boardId), noassignees].forEach((assigneeId: string) => {
        result[priorityId][assigneeId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get priority for this card
      const cardPriority = card.priority ?? 0;

      // Get assignees for this card
      const cardAssignees = getCardAssignees(card, noassignees);

      // Add card to appropriate assignee for this priority
      if (!result[cardPriority]) {
        result[cardPriority] = {};
      }

      cardAssignees.forEach((assigneeId) => {
        if (!result[cardPriority][assigneeId]) {
          result[cardPriority][assigneeId] = { cardIds: [] };
        }
        if (card.id) {
          result[cardPriority][assigneeId].cardIds.push(card.id);
        }
      });
    });

    return result;
  },

  // Group by Priority -> Tags
  groupByPriorityTags: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const priorities = getters.groupByPriority(boardId);
    const projectTags = getters.getProjectTags(getters.getCurrentProjectId) ?? [];
    const workspaceTags = getters.getWorkspaceTags(getters.getTeamID);
    const projectWorkspaceTags = [...projectTags, ...workspaceTags];
    const notags = `${boardId}-notags`;

    // Result will be { priorityId: { tagId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...projectWorkspaceTags, notags]));

    // Initialize structure for all priorities
    priorities?.order?.forEach((priorityId: string) => {
      result[priorityId] = {};

      // Initialize all tags for this priority
      [...projectWorkspaceTags, notags].forEach((tagId: string) => {
        result[priorityId][tagId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get priority for this card
      const cardPriority = card.priority ?? 0;

      // Add card to appropriate tag for this priority
      if (!result[cardPriority]) {
        result[cardPriority] = {};
      }

      if (!card.tags || card.tags.length === 0) {
        if (!result[cardPriority][notags]) {
          result[cardPriority][notags] = { cardIds: [] };
        }
        if (card.id) {
          result[cardPriority][notags].cardIds.push(card.id);
        }
      } else {
        card.tags.forEach((tag: Tag) => {
          if (!tag.id || !state.tags[tag.id]) return;

          if (!result[cardPriority][tag.id]) {
            result[cardPriority][tag.id] = { cardIds: [] };
          }
          if (card.id) {
            result[cardPriority][tag.id].cardIds.push(card.id);
          }
        });
      }
    });

    return result;
  },

  // Group by Priority -> Status
  groupByPriorityStatus: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const priorities = getters.groupByPriority(boardId);
    const board = getters.getBoardById(boardId);

    // Result will be { priorityId: { statusId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    if (board?.sprint_id) {
      result.order = board.lists.map((list: List) => list.id);
    } else {
      result.order = board?.list_order ?? [];
    }

    // Initialize structure for all priorities
    priorities?.order?.forEach((priorityId: string) => {
      result[priorityId] = {};

      // Initialize all statuses for this priority
      board?.list_order?.forEach((statusId: string) => {
        result[priorityId][statusId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get priority for this card
      const cardPriority = card.priority ?? 0;

      // Get status for this card
      const cardStatus = card.list_id ?? board?.list_order?.[0];
      if (!cardStatus) return;

      // Add card to appropriate status for this priority
      if (!result[cardPriority]) {
        result[cardPriority] = {};
      }

      if (!result[cardPriority][cardStatus]) {
        result[cardPriority][cardStatus] = { cardIds: [] };
      }
      if (card.id) {
        result[cardPriority][cardStatus].cardIds.push(card.id);
      }
    });

    return result;
  },

  // Group by Epics -> Assignees
  groupByEpicsAssignees: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const epics = getters.groupByEpics(boardId);
    const noassignees = `${boardId}-unassigned`;

    // Result will be { epicId: { assigneeId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...getters.getAssigneeLists(boardId), noassignees]));

    // Initialize structure for all epics
    epics?.order?.forEach((epicId: string) => {
      result[epicId] = {};

      // Initialize all assignees for this epic
      [...getters.getAssigneeLists(boardId), noassignees].forEach((assigneeId: string) => {
        result[epicId][assigneeId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get epic for this card
      const cardEpicId = card.epic?.id ?? `${boardId}-noproject`;

      // Get assignees for this card
      const cardAssignees = getCardAssignees(card, noassignees);

      // Add card to appropriate assignee for this epic
      if (!result[cardEpicId]) {
        result[cardEpicId] = {};
      }

      cardAssignees.forEach((assigneeId) => {
        if (!result[cardEpicId][assigneeId]) {
          result[cardEpicId][assigneeId] = { cardIds: [] };
        }
        if (card.id) {
          result[cardEpicId][assigneeId].cardIds.push(card.id);
        }
      });
    });

    return result;
  },

  // Group by Epics -> Tags
  groupByEpicsTags: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const epics = getters.groupByEpics(boardId);
    const projectTags = getters.getProjectTags(getters.getCurrentProjectId) ?? [];
    const workspaceTags = getters.getWorkspaceTags(getters.getTeamID);
    const projectWorkspaceTags = [...projectTags, ...workspaceTags];
    const notags = `${boardId}-notags`;

    // Result will be { epicId: { tagId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    result.order = Array.from(new Set([...projectWorkspaceTags, notags]));

    // Initialize structure for all epics
    epics?.order?.forEach((epicId: string) => {
      result[epicId] = {};

      // Initialize all tags for this epic
      [...projectWorkspaceTags, notags].forEach((tagId: string) => {
        result[epicId][tagId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get epic for this card
      const cardEpicId = card.epic?.id ?? `${boardId}-noproject`;

      // Add card to appropriate tag for this epic
      if (!result[cardEpicId]) {
        result[cardEpicId] = {};
      }

      if (!card.tags || card.tags.length === 0) {
        if (!result[cardEpicId][notags]) {
          result[cardEpicId][notags] = { cardIds: [] };
        }
        if (card.id) {
          result[cardEpicId][notags].cardIds.push(card.id);
        }
      } else {
        card.tags.forEach((tag: Tag) => {
          if (!tag.id || !state.tags[tag.id]) return;

          if (!result[cardEpicId][tag.id]) {
            result[cardEpicId][tag.id] = { cardIds: [] };
          }
          if (card.id) {
            result[cardEpicId][tag.id].cardIds.push(card.id);
          }
        });
      }
    });

    return result;
  },

  // Group by Epics -> Status
  groupByEpicsStatus: (state, getters) => (boardId: string) => {
    const cards: string[] = getters.boardsCardsIds[boardId] ?? [];
    const epics = getters.groupByEpics(boardId);
    const board = getters.getBoardById(boardId);

    // Result will be { epicId: { statusId: { cardIds: [] } } }
    const result: Record<string, Record<string, { cardIds: string[] }>> & { order?: string[] } = {};
    if (board?.sprint_id) {
      result.order = board.lists.map((list: List) => list.id);
    } else {
      result.order = board?.list_order ?? [];
    }

    // Initialize structure for all epics
    epics?.order?.forEach((epicId: string) => {
      result[epicId] = {};

      // Initialize all statuses for this epic
      board?.list_order?.forEach((statusId: string) => {
        result[epicId][statusId] = { cardIds: [] };
      });
    });

    // Process all cards
    cards.forEach((cardId: string) => {
      const card: Card | undefined = state.cards[cardId];
      if (!card?.id) return;

      // Get epic for this card
      const cardEpicId = card.epic?.id ?? `${boardId}-noproject`;

      // Get status for this card
      const cardStatus = card.list_id ?? board?.list_order?.[0];
      if (!cardStatus) return;

      // Add card to appropriate status for this epic
      if (!result[cardEpicId]) {
        result[cardEpicId] = {};
      }

      if (!result[cardEpicId][cardStatus]) {
        result[cardEpicId][cardStatus] = { cardIds: [] };
      }
      if (card.id) {
        result[cardEpicId][cardStatus].cardIds.push(card.id);
      }
    });

    return result;
  },
} as GetterTree<State, any>;
